import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { User, Task, Client } from '../types';
import { API_BASE_URL } from '../utils/api';

interface TaskFormData {
  id?: number;
  title: string;
  client: string;
  address_line1: string;
  address_line2?: string;
  postcode: string;
  description?: string;
  engineer_notes?: string;
  job_type?: string;
  scheduled_date?: string;
  start_time?: string;
  end_time?: string;
  status?: string;
  assigned_to?: number | null;
  invoice_number?: string;
}

interface ClientFormData {
  name: string;
  email?: string;
  phone?: string;
  address_line1?: string;
  address_line2?: string;
  postcode?: string;
  client_type?: string;
}

interface TaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (task: TaskFormData) => void;
  task?: Task;
  users: User[];
}

const TaskModal: React.FC<TaskModalProps> = ({ isOpen, onClose, onSubmit, task, users }) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [clientFormData, setClientFormData] = useState<ClientFormData>({
    name: '',
    email: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    postcode: '',
    client_type: ''
  });
  const [clientFormError, setClientFormError] = useState('');
  const [creatingClient, setCreatingClient] = useState(false);
  const engineerNotesRef = useRef<HTMLTextAreaElement>(null);

  // Generate time options in half-hour increments (7:00 AM to 6:00 PM only)
  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 7; hour <= 18; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        options.push(timeString);
      }
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  // Auto-resize function for engineer notes textarea
  const autoResizeTextarea = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  };
  const [formData, setFormData] = useState<TaskFormData>({
    title: '',
    client: '',
    address_line1: '',
    address_line2: '',
    postcode: '',
    description: '',
    engineer_notes: '',
    job_type: '',
    scheduled_date: '',
    start_time: '',
    end_time: '',
    status: 'TBC',
    assigned_to: null,
    invoice_number: '',
  });

  useEffect(() => {
    if (isOpen) {
      fetchClients();
    }
  }, [isOpen]);

  // Auto-resize engineer notes textarea when content changes
  useEffect(() => {
    if (engineerNotesRef.current) {
      autoResizeTextarea(engineerNotesRef.current);
    }
  }, [formData.engineer_notes]);

  useEffect(() => {
    const filtered = clients.filter(client =>
      client.name.toLowerCase().includes(formData.client.toLowerCase())
    );
    setFilteredClients(filtered.slice(0, 5)); // Show max 5 results
  }, [clients, formData.client]);

  useEffect(() => {
    if (task) {
      // Convert time format from HH:MM:SS to HH:MM for form dropdowns
      const formatTimeForForm = (timeString?: string) => {
        console.log('Formatting time for form:', timeString);
        if (!timeString) return '';
        
        // Handle full datetime strings (e.g., "0000-01-01T05:00:00Z")
        if (timeString.includes('T')) {
          const timePart = timeString.split('T')[1];
          if (timePart.includes(':')) {
            const parts = timePart.split(':');
            const formatted = `${parts[0]}:${parts[1]}`;
            console.log('Formatted datetime to:', formatted);
            return formatted;
          }
          return timePart;
        }
        
        // Handle simple time strings (e.g., "05:00:00")
        if (timeString.includes(':')) {
          const parts = timeString.split(':');
          const formatted = `${parts[0]}:${parts[1]}`;
          console.log('Formatted time to:', formatted);
          return formatted;
        }
        
        console.log('Time unchanged:', timeString);
        return timeString;
      };

      // Format date for form input (needs to be YYYY-MM-DD format)
      const formatDateForForm = (dateString?: string) => {
        console.log('Formatting date for form:', dateString);
        if (!dateString) return '';
        
        // If it's already in YYYY-MM-DD format, return as is
        if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
          console.log('Date already in correct format:', dateString);
          return dateString;
        }
        
        // If it's a full datetime string, extract the date part
        if (dateString.includes('T')) {
          const datePart = dateString.split('T')[0];
          console.log('Extracted date from datetime:', datePart);
          return datePart;
        }
        
        // Try to parse and format the date
        try {
          const date = new Date(dateString);
          if (!isNaN(date.getTime())) {
            const formatted = date.toISOString().split('T')[0];
            console.log('Formatted date to:', formatted);
            return formatted;
          }
        } catch (error) {
          console.error('Error parsing date:', error);
        }
        
        console.log('Date unchanged:', dateString);
        return dateString;
      };

      console.log('Task data received:', {
        id: task.id,
        scheduled_date: task.scheduled_date,
        start_time: task.start_time,
        end_time: task.end_time
      });

      setFormData({
        ...task,
        scheduled_date: formatDateForForm(task.scheduled_date),
        start_time: formatTimeForForm(task.start_time),
        end_time: formatTimeForForm(task.end_time),
        assigned_to: task.assigned_to,
        invoice_number: task.invoice_number || ''
      });
    } else {
      setFormData({
        title: '',
        client: '',
        address_line1: '',
        address_line2: '',
        postcode: '',
        description: '',
        engineer_notes: '',
        job_type: '',
        scheduled_date: '',
        start_time: '',
        end_time: '',
        status: 'TBC',
        assigned_to: null,
        invoice_number: '',
      });
    }
  }, [task]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/clients`);
      const clientData = response.data;
      if (Array.isArray(clientData)) {
        setClients(clientData);
      } else {
        console.warn('Clients API returned non-array data:', clientData);
        setClients([]);
      }
    } catch (err: any) {
      console.error('Failed to fetch clients:', err);
      setClients([]);
    }
  };

  const handleClientFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setClientFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddClient = async (e: React.FormEvent) => {
    e.preventDefault();
    setClientFormError('');
    setCreatingClient(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/clients`, clientFormData);
      const newClient = response.data;
      
      // Add the new client to the clients list
      setClients(prev => [...prev, newClient]);
      
      // Auto-select the new client and populate address fields
      setFormData(prev => ({
        ...prev,
        client: newClient.name,
        address_line1: newClient.address_line1 || '',
        address_line2: newClient.address_line2 || '',
        postcode: newClient.postcode || ''
      }));
      
      // Reset client form and close modal
      setClientFormData({
        name: '',
        email: '',
        phone: '',
        address_line1: '',
        address_line2: '',
        postcode: '',
        client_type: ''
      });
      setShowAddClientModal(false);
    } catch (err: any) {
      console.error('Failed to create client:', err);
      setClientFormError(err.response?.data?.error || 'Failed to create client');
    } finally {
      setCreatingClient(false);
    }
  };

  const openAddClientModal = () => {
    setShowAddClientModal(true);
    setShowClientDropdown(false);
  };

  const closeAddClientModal = () => {
    setShowAddClientModal(false);
    setClientFormError('');
    setClientFormData({
      name: '',
      email: '',
      phone: '',
      address_line1: '',
      address_line2: '',
      postcode: '',
      client_type: ''
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'assigned_to' ? (value ? parseInt(value) : null) : value,
    }));

    // Auto-resize engineer notes textarea when typing
    if (name === 'engineer_notes' && e.target instanceof HTMLTextAreaElement) {
      autoResizeTextarea(e.target);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal">
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="modal-title">{task ? 'Edit Task' : 'Add New Task'}</h2>
          <button onClick={onClose} className="close-btn">&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Job Type *</label>
            <select
              name="job_type"
              value={formData.job_type}
              onChange={handleChange}
              className="form-select"
              required
            >
              <option value="">Select job type...</option>
              <option value="Gas Safety Inspection">Gas Safety Inspection</option>
              <option value="Plumbing">Plumbing</option>
              <option value="Heating">Heating</option>
              <option value="Quote">Quote</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Scheduled Date</label>
            <input
              type="date"
              name="scheduled_date"
              value={formData.scheduled_date}
              onChange={handleChange}
              className="form-input"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Between</label>
              <select
                name="start_time"
                value={formData.start_time}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">Select start time...</option>
                {timeOptions.map(time => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">And</label>
              <select
                name="end_time"
                value={formData.end_time}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">Select end time...</option>
                {timeOptions.map(time => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="form-input"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Client *</label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <input
                  type="text"
                  name="client"
                  value={formData.client}
                  onChange={(e) => {
                    handleChange(e);
                    setShowClientDropdown(true);
                  }}
                  onFocus={() => setShowClientDropdown(true)}
                  onBlur={() => setTimeout(() => setShowClientDropdown(false), 200)}
                  className="form-input"
                  placeholder="Search clients..."
                  required
                />
                {showClientDropdown && filteredClients.length > 0 && formData.client && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    borderTop: 'none',
                    borderRadius: '0 0 4px 4px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    {filteredClients.map((client) => (
                      <div
                        key={client.id}
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            client: client.name,
                            address_line1: client.address_line1 || '',
                            address_line2: client.address_line2 || '',
                            postcode: client.postcode || ''
                          }));
                          setShowClientDropdown(false);
                        }}
                        style={{
                          padding: '0.75rem 1rem',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f0f0f0'
                        }}
                        onMouseEnter={(e) => (e.target as HTMLDivElement).style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={(e) => (e.target as HTMLDivElement).style.backgroundColor = 'white'}
                      >
                        <div style={{ fontWeight: 'bold' }}>{client.name}</div>
                        {client.address_line1 && (
                          <div style={{ fontSize: '0.875rem', color: '#666' }}>
                            {client.address_line1}{client.address_line2 && `, ${client.address_line2}`}
                            {client.postcode && ` ${client.postcode}`}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={openAddClientModal}
                className="btn btn-primary"
                style={{ 
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap'
                }}
              >
                Add Client
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Address Line 1 *</label>
            <input
              type="text"
              name="address_line1"
              value={formData.address_line1}
              onChange={handleChange}
              className="form-input"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Address Line 2</label>
            <input
              type="text"
              name="address_line2"
              value={formData.address_line2}
              onChange={handleChange}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Postcode *</label>
            <input
              type="text"
              name="postcode"
              value={formData.postcode}
              onChange={handleChange}
              className="form-input"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="form-select"
            >
              <option value="TBC">TBC</option>
              <option value="Booked">Booked</option>
              <option value="Completed">Completed</option>
              <option value="Invoiced">Invoiced</option>
            </select>
          </div>

          {/* Invoice Number Field - Only show when status is Invoiced */}
          {formData.status?.toLowerCase() === 'invoiced' && (
            <div className="form-group">
              <label className="form-label">Invoice Number</label>
              <input
                type="text"
                name="invoice_number"
                value={formData.invoice_number || ''}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter invoice number"
              />
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Assign to Engineer</label>
            <select
              name="assigned_to"
              value={formData.assigned_to || ''}
              onChange={handleChange}
              className="form-select"
            >
              <option value="">Unassigned</option>
              {(users || []).filter(user => user.id).map(user => (
                <option key={user.id} value={user.id}>
                  {user.first_name} {user.last_name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="form-textarea"
              rows={4}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Engineer Notes</label>
            <textarea
              ref={engineerNotesRef}
              name="engineer_notes"
              value={formData.engineer_notes}
              onChange={handleChange}
              className="form-textarea"
              rows={1}
              placeholder="Notes for the assigned engineer..."
              style={{ 
                resize: 'none', 
                overflow: 'hidden', 
                minHeight: '38px'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="btn">
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {task ? 'Update' : 'Create'} Task
            </button>
          </div>
        </form>
      </div>

      {/* Add Client Modal */}
      {showAddClientModal && (
        <div className="modal" style={{ zIndex: 2000 }}>
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Add New Client</h2>
              <button onClick={closeAddClientModal} className="close-btn">&times;</button>
            </div>

            {clientFormError && <div className="error-message">{clientFormError}</div>}

            <form onSubmit={handleAddClient}>
              <div className="form-group">
                <label className="form-label">Type of Client *</label>
                <select
                  name="client_type"
                  value={clientFormData.client_type}
                  onChange={handleClientFormChange}
                  className="form-input"
                  required
                  disabled={creatingClient}
                >
                  <option value="">Select client type...</option>
                  <option value="Letting Agent">Letting Agent</option>
                  <option value="Private">Private</option>
                  <option value="Landlord">Landlord</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Client Name *</label>
                <input
                  type="text"
                  name="name"
                  value={clientFormData.name}
                  onChange={handleClientFormChange}
                  className="form-input"
                  required
                  disabled={creatingClient}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  name="email"
                  value={clientFormData.email}
                  onChange={handleClientFormChange}
                  className="form-input"
                  disabled={creatingClient}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={clientFormData.phone}
                  onChange={handleClientFormChange}
                  className="form-input"
                  disabled={creatingClient}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Address Line 1</label>
                <input
                  type="text"
                  name="address_line1"
                  value={clientFormData.address_line1}
                  onChange={handleClientFormChange}
                  className="form-input"
                  disabled={creatingClient}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Address Line 2</label>
                <input
                  type="text"
                  name="address_line2"
                  value={clientFormData.address_line2}
                  onChange={handleClientFormChange}
                  className="form-input"
                  disabled={creatingClient}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Postcode</label>
                <input
                  type="text"
                  name="postcode"
                  value={clientFormData.postcode}
                  onChange={handleClientFormChange}
                  className="form-input"
                  disabled={creatingClient}
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button 
                  type="button" 
                  onClick={closeAddClientModal} 
                  className="btn"
                  disabled={creatingClient}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={creatingClient}
                >
                  {creatingClient ? 'Creating...' : 'Create Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskModal;